import { BLOCKED_SUBSTRINGS, RESERVED_NAMES } from "./username.blocklist";

/**
 * Validation for the two names a player is seen under, shared by the username
 * claim (`playerDoc.ts`) and the match nickname field — see
 * `docs/CLOUD_SAVES_DESIGN.md` §7.
 *
 * Pure, and no longer the only place either name is screened. Both have a
 * server-side twin generated from the same word lists
 * (`scripts/gen-username-guards.mjs`): a username claim goes straight to
 * Firestore, so the security rules carry the screen; a nickname arrives over
 * HTTP, so the game server does (`internal/nickname`). This module is the
 * copy that can say WHY before anything is submitted, which is the only thing
 * a client-side check is good for.
 *
 * The two differ in one respect: a nickname may contain spaces and a username
 * may not, so a claimed username is always usable as-is for a nickname.
 */
export type UsernameValidation = { ok: true; name: string } | { ok: false; reason: string };

const CHARSET_RE = /^[A-Za-z0-9_-]+$/;
/** The nickname charset: the same, plus the space. */
const NICKNAME_CHARSET_RE = /^[A-Za-z0-9 _-]+$/;
const EDGE_SEPARATOR_RE = /^[-_]|[-_]$/;

/** Leetspeak folded by `normalizeForScreening`, mapping digit/symbol stand-ins to letters. */
const LEET_MAP: Record<string, string> = {
  "0": "o",
  "1": "i",
  "3": "e",
  "4": "a",
  "5": "s",
  "7": "t",
  "8": "b",
  "@": "a",
  "$": "s",
};

/**
 * Folds a raw name down to the form the profanity check runs against:
 * lowercase, separators removed (so "f_u_c_k" reads the same as "fuck"), and
 * common leetspeak stand-ins mapped back to the letters they're impersonating.
 * Exported for tests; not used for the reserved-name check, which matches the
 * name a player actually sees rather than an evasion-proofed version of it.
 *
 * The space is stripped along with `-` and `_` for the nickname's sake — that
 * charset admits spaces, and "s h i t" is the same evasion as "s-h-i-t".
 */
export function normalizeForScreening(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[-_ ]/g, "")
    .split("")
    .map((ch) => LEET_MAP[ch] ?? ch)
    .join("");
}

function isProfane(screened: string): boolean {
  return BLOCKED_SUBSTRINGS.some((term) => screened.includes(term));
}

function validate(raw: string, charset: RegExp, charsetReason: string): UsernameValidation {
  const trimmed = raw.trim();

  if (trimmed.length < 3 || trimmed.length > 10) {
    return { ok: false, reason: "Must be 3–10 characters" };
  }
  if (!charset.test(trimmed)) {
    return { ok: false, reason: charsetReason };
  }
  if (EDGE_SEPARATOR_RE.test(trimmed)) {
    return { ok: false, reason: "Can't start or end with - or _" };
  }
  if (RESERVED_NAMES.includes(trimmed.toLowerCase())) {
    return { ok: false, reason: "That name isn't available" };
  }
  if (isProfane(normalizeForScreening(trimmed))) {
    return { ok: false, reason: "That name isn't allowed" };
  }
  return { ok: true, name: trimmed };
}

export function validateUsername(raw: string): UsernameValidation {
  return validate(raw, CHARSET_RE, "Letters, numbers, - and _ only");
}

/**
 * The same screen over the nickname charset. Its server-side twin is
 * `ValidateNickname` in the game server, which is what actually decides whether
 * a name reaches a table.
 */
export function validateNickname(raw: string): UsernameValidation {
  return validate(raw, NICKNAME_CHARSET_RE, "Letters, numbers, spaces, - and _ only");
}
